/**
 * The frame and the parts the client record's printable documents share:
 * the Face Sheet (the client as of today) and the Client history (what
 * changed over time).
 *
 * PrintableSheetDialog is the overlay: a screen-only action bar (Close,
 * Print, Download PDF) over the sheet, rendered through a portal beside
 * #root. While it is mounted the body carries `face-sheet-open`, the class
 * the print scope in index.css keys off: printing shows only the open
 * sheet, and the scope's rules keep a section, a list row or a heading
 * from splitting across a page. Both documents use the same classes, so
 * both print under the same rules.
 *
 * Download PDF captures the sheet element by its id with html2pdf.js,
 * loaded lazily (an approved minimum-deps exception). pagebreak's
 * avoid-all mode keeps a section whole; scrollX and scrollY pin the
 * capture to the top of the window. A page opened before a deploy cannot
 * load the PDF maker and says to reload; any other failure falls back to
 * the browser's print dialog.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/primitives/Button';
import { useDialogDismiss } from '@/components/primitives/useDialogDismiss';
import { proseDate } from '@/lib/proseDate';
import type { HospitalEventRow, MedicationRow } from '@/lib/clientRecord';
import { humanizeToken } from '@/lib/fieldLabels';
import { displayValue } from '@/lib/valueCase';
import { errorText, FALLBACK_SUPPORT_CONTACT } from '@/lib/errorCopy';
import { loadModule, isModuleLoadError } from '@/lib/loadModule';

/** One quiet section scaffold: caps header over a hairline. */
export function SheetSection({
  title,
  testId,
  className,
  children,
}: {
  title: string;
  testId: string;
  className?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section data-testid={testId} className={className}>
      <h3 className="text-[0.65625rem] font-semibold uppercase tracking-[0.14em] text-ink-soft border-b border-sage-300 pb-1 mb-1.5">
        {title}
      </h3>
      {children}
    </section>
  );
}

export const NONE = <p className="text-[0.78125rem] italic text-ink-soft">None recorded</p>;

/** A section's list, or its quiet empty line. */
export function SheetList({ items, empty }: { items: Array<{ key: string; body: ReactNode }>; empty: string }): ReactNode {
  if (items.length === 0) return <p className="text-[0.78125rem] italic text-ink-soft">{empty}</p>;
  return (
    <ul className="flex flex-col gap-1 text-[0.78125rem]">
      {items.map((it) => (
        <li key={it.key}>{it.body}</li>
      ))}
    </ul>
  );
}

/** One hospital event's line: kind, date (or its words), facility, note. */
export function hospitalLine(h: HospitalEventRow): ReactNode {
  const when = h.eventOn ? proseDate(h.eventOn) : h.dateText ? displayValue(h.dateText) : null;
  return (
    <>
      <span className="font-semibold">{humanizeToken(h.kind)}</span>
      {when && ` · ${when}`}
      {h.facility && ` · ${displayValue(h.facility)}`}
      {h.note && <span className="text-ink-soft">{`: ${displayValue(h.note)}`}</span>}
    </>
  );
}

/** A past medication's dose: "{first} to {last}" when it was created at a
 *  different dose, else the dose. */
export function doseSpan(m: MedicationRow): string {
  const dose = displayValue(m.dose ?? '');
  if (m.firstDose && m.firstDose.trim().toLowerCase() !== (m.dose ?? '').trim().toLowerCase()) {
    return `${displayValue(m.firstDose)} to ${dose}`;
  }
  return dose;
}

export interface PrintableSheetDialogProps {
  /** The dialog's accessible name and its heading ("Face Sheet"). */
  title: string;
  /** The line under the heading. */
  blurb: ReactNode;
  /** The test id prefix: `{prefix}-dialog`, `{prefix}-print`,
   *  `{prefix}-download`, `{prefix}-download-error`. */
  testIdPrefix: string;
  /** The id of the element Download PDF captures (`{prefix}-print`'s target). */
  printElementId: string;
  /** The PDF's file name stem ("FaceSheet"); the client's name follows. */
  pdfStem: string;
  /** The client's display name, for the file name. */
  clientName: string;
  onClose: () => void;
  /** The sheet itself: the element with `printElementId`. */
  children: ReactNode;
}

export function PrintableSheetDialog({
  title,
  blurb,
  testIdPrefix,
  printElementId,
  pdfStem,
  clientName,
  onClose,
  children,
}: PrintableSheetDialogProps): ReactNode {
  const ref = useDialogDismiss<HTMLDivElement>(onClose);
  const [downloading, setDownloading] = useState(false);
  const [stale, setStale] = useState(false);

  async function onDownloadPdf(): Promise<void> {
    const el = document.getElementById(printElementId);
    if (el === null) return;
    setDownloading(true);
    setStale(false);
    try {
      const html2pdf = (await loadModule('face_sheet', () => import('html2pdf.js'))).default;
      const safeName = clientName.replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '') || 'client';
      const worker = html2pdf();
      // html2pdf.js's type.d.ts omits `pagebreak` and does not export its
      // options interface, so the `set` parameter type is widened here.
      type SetOptions = Parameters<typeof worker.set>[0];
      const pdfOptions: SetOptions & { pagebreak: { mode: string[] } } = {
        margin: 0.5,
        filename: `${pdfStem}-${safeName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        pagebreak: { mode: ['avoid-all', 'css'] },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', scrollX: 0, scrollY: 0 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
      };
      await worker.set(pdfOptions).from(el).save();
    } catch (e) {
      if (isModuleLoadError(e)) setStale(true);
      else window.print();
    } finally {
      setDownloading(false);
    }
  }

  // The print scope in index.css keys off this class: while a sheet is
  // open, printing shows only the sheet. Removed on close.
  useEffect(() => {
    document.body.classList.add('face-sheet-open');
    return () => document.body.classList.remove('face-sheet-open');
  }, []);

  const overlay = (
    <div
      className="face-sheet-overlay fixed inset-0 z-[70] flex items-start justify-center bg-ink/30 px-4 py-8 overflow-auto"
      onClick={onClose}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        data-testid={`${testIdPrefix}-dialog`}
        onClick={(e) => e.stopPropagation()}
        className="face-sheet-panel w-full max-w-3xl bg-paper rounded-2xl shadow-sh3 border border-sage-800/[8%] p-6 focus:outline-none"
      >
        {/* Screen-only action bar (the print scope hides it). */}
        <div className="face-sheet-actions mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-lg text-sage-800">{title}</h3>
            <p className="text-xs text-ink-soft">{blurb}</p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
            <Button variant="ghost" size="sm" data-testid={`${testIdPrefix}-print`} onClick={() => window.print()}>
              Print
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={downloading}
              data-testid={`${testIdPrefix}-download`}
              onClick={() => void onDownloadPdf()}
            >
              Download PDF
            </Button>
          </div>
        </div>
        {stale && (
          <p role="alert" className="mb-3 text-xs text-warm-700" data-testid={`${testIdPrefix}-download-error`}>
            {errorText('face_sheet', 'module_stale', FALLBACK_SUPPORT_CONTACT)}
          </p>
        )}
        {children}
      </div>
    </div>
  );

  // PORTAL (sibling of #root): the print scope hides the app and prints
  // only this subtree.
  return createPortal(overlay, document.body);
}
